import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    IconButton,
    Paper,
    Checkbox,
    FormControlLabel,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    AutoAwesome as AiIcon
} from '@mui/icons-material';
import QuizGenerator from '../QuizGenerator';
import RichTextInput from './RichTextInput';

interface MultipleChoiceEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface Question {
    id: string;
    question: string;
    options: { id: string; text: string; isCorrect: boolean }[];
    explanation?: string;
}

const MultipleChoiceEditor: React.FC<MultipleChoiceEditorProps> = ({ content, onChange }) => {
    // Ensure questions have valid structure with options array
    const questions: Question[] = (content?.questions || []).map((q: any) => ({
        ...q,
        options: Array.isArray(q.options) ? q.options : []
    }));
    const learningContent = content?.learningContent || '';
    const [generatorOpen, setGeneratorOpen] = useState(false);

    const addQuestion = () => {
        const newQuestion: Question = {
            id: Date.now().toString(),
            question: '',
            options: [
                { id: Date.now() + '-1', text: '', isCorrect: false },
                { id: Date.now() + '-2', text: '', isCorrect: false }
            ],
            explanation: ''
        };
        onChange({ ...content, questions: [...questions, newQuestion] });
    };

    const updateQuestion = (questionId: string, field: string, value: any) => {
        const updatedQuestions = questions.map((q) =>
            q.id === questionId ? { ...q, [field]: value } : q
        );
        onChange({ ...content, questions: updatedQuestions });
    };

    const deleteQuestion = (questionId: string) => {
        const updatedQuestions = questions.filter((q) => q.id !== questionId);
        onChange({ ...content, questions: updatedQuestions });
    };

    const addOption = (questionId: string) => {
        const updatedQuestions = questions.map((q) => {
            if (q.id === questionId) {
                return {
                    ...q,
                    options: [
                        ...(q.options || []),
                        { id: Date.now().toString(), text: '', isCorrect: false }
                    ]
                };
            }
            return q;
        });
        onChange({ ...content, questions: updatedQuestions });
    };

    const updateOption = (questionId: string, optionId: string, field: string, value: any) => {
        const updatedQuestions = questions.map((q) => {
            if (q.id === questionId) {
                return {
                    ...q,
                    options: (q.options || []).map((opt) =>
                        opt.id === optionId ? { ...opt, [field]: value } : opt
                    )
                };
            }
            return q;
        });
        onChange({ ...content, questions: updatedQuestions });
    };

    const deleteOption = (questionId: string, optionId: string) => {
        const updatedQuestions = questions.map((q) => {
            if (q.id === questionId) {
                return {
                    ...q,
                    options: (q.options || []).filter((opt) => opt.id !== optionId)
                };
            }
            return q;
        });
        onChange({ ...content, questions: updatedQuestions });
    };

    const handleQuestionsGenerated = (generatedQuestions: any[]) => {
        const newQuestions: Question[] = generatedQuestions.map((gq, index) => ({
            id: Date.now() + '-' + index,
            question: gq.question || '',
            options: gq.options?.map((opt: string, optIndex: number) => ({
                id: Date.now() + '-' + index + '-' + optIndex,
                text: opt,
                isCorrect: gq.correctAnswer === optIndex
            })) || [],
            explanation: gq.explanation || ''
        }));
        onChange({ ...content, questions: [...questions, ...newQuestions] });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Multiple Choice Fragen</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AiIcon />}
                        onClick={() => setGeneratorOpen(true)}
                    >
                        KI-Generator
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addQuestion}>
                        Frage hinzufügen
                    </Button>
                </Box>
            </Box>

            <RichTextInput
                label="Lerninhalt (optional)"
                value={learningContent}
                onChange={(val) => onChange({ ...content, learningContent: val })}
                helperText="Dieser formatierte Text wird im Player über dem Quiz angezeigt."
                placeholder="Optionaler Lernstoff, der über dem Quiz angezeigt wird..."
            />

            {questions.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        Noch keine Fragen vorhanden. Klicken Sie auf "Frage hinzufügen" um zu beginnen.
                    </Typography>
                </Paper>
            ) : (
                questions.map((question, qIndex) => (
                    <Paper key={question.id} sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Frage {qIndex + 1}
                            </Typography>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteQuestion(question.id)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>

                        <TextField
                            label="Frage"
                            fullWidth
                            multiline
                            rows={2}
                            value={question.question}
                            onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Antwortmöglichkeiten:
                        </Typography>

                        {(question.options || []).map((option, oIndex) => (
                            <Box key={option.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={option.isCorrect}
                                            onChange={(e) =>
                                                updateOption(question.id, option.id, 'isCorrect', e.target.checked)
                                            }
                                        />
                                    }
                                    label="Richtig"
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={`Option ${oIndex + 1}`}
                                    value={option.text}
                                    onChange={(e) =>
                                        updateOption(question.id, option.id, 'text', e.target.value)
                                    }
                                />
                                {(question.options?.length || 0) > 2 && (
                                    <IconButton
                                        size="small"
                                        onClick={() => deleteOption(question.id, option.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        ))}

                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addOption(question.id)}
                            sx={{ mt: 1 }}
                        >
                            Option hinzufügen
                        </Button>

                        <Divider sx={{ my: 2 }} />

                        <TextField
                            label="Erklärung (optional)"
                            fullWidth
                            multiline
                            rows={2}
                            value={question.explanation || ''}
                            onChange={(e) => updateQuestion(question.id, 'explanation', e.target.value)}
                            helperText="Wird nach der Beantwortung angezeigt"
                        />
                    </Paper>
                ))
            )}

            <QuizGenerator
                open={generatorOpen}
                onClose={() => setGeneratorOpen(false)}
                onQuestionsGenerated={handleQuestionsGenerated}
                defaultQuestionType="multiple_choice"
                lessonLearningContent={learningContent}
            />
        </Box>
    );
};

export default MultipleChoiceEditor;
